import { motion, useReducedMotion } from "motion/react";
import { cn } from "@eduviaos/ui-kit";
import type { LucideIcon } from "lucide-react";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
}

/**
 * Floating tab bar with a raised centre action button (FAB). Two tabs sit
 * on each side of the FAB. Constrained to the app column width and pinned
 * to the viewport bottom with safe-area padding.
 */
export function BottomNav({
  items,
  activeId,
  onSelect,
  fab,
  learnerLocale = true,
}: {
  items: NavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  fab?: { label: string; icon: LucideIcon; onClick: () => void };
  /** Read the learner's saved language. Off for the Team persona (no learner prefs). */
  learnerLocale?: boolean;
}) {
  const reduce = useReducedMotion();
  const { strings: s } = usePortalLocale(learnerLocale);

  if (!fab) {
    return (
      <nav
        aria-label={s.primaryNavAria}
        className="pb-safe pointer-events-none fixed inset-x-0 bottom-0 z-30"
      >
        <div className="border-border/60 bg-card/90 pointer-events-auto relative mx-auto mb-3 flex max-w-md items-center justify-around gap-1 rounded-[1.5rem] border px-3 py-2 shadow-[var(--shadow-float)] backdrop-blur-xl">
          {items.map((item) => (
            <NavButton
              key={item.id}
              item={item}
              active={item.id === activeId}
              onSelect={onSelect}
              reduce={!!reduce}
            />
          ))}
        </div>
      </nav>
    );
  }

  const mid = Math.ceil(items.length / 2);
  const left = items.slice(0, mid);
  const right = items.slice(mid);
  const FabIcon = fab.icon;

  return (
    <nav
      aria-label={s.primaryNavAria}
      className="pb-safe pointer-events-none fixed inset-x-0 bottom-0 z-30"
    >
      <div className="border-border/60 bg-card/90 pointer-events-auto relative mx-auto mb-3 flex max-w-md items-center justify-between gap-1 rounded-[1.5rem] border px-3 py-2 shadow-[var(--shadow-float)] backdrop-blur-xl">
        <div className="flex flex-1 justify-around">
          {left.map((item) => (
            <NavButton
              key={item.id}
              item={item}
              active={item.id === activeId}
              onSelect={onSelect}
              reduce={!!reduce}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={fab.onClick}
          aria-label={fab.label}
          className="from-primary bg-primary relative -mt-8 grid size-14 shrink-0 place-items-center rounded-full bg-gradient-to-br to-[color-mix(in_oklch,var(--color-primary)_60%,oklch(0.66_0.2_340))] text-white shadow-[var(--shadow-float)] ring-4 ring-[var(--color-background)] transition-transform duration-150 ease-out hover:-translate-y-0.5 active:scale-95"
        >
          <FabIcon className="size-6" />
        </button>

        <div className="flex flex-1 justify-around">
          {right.map((item) => (
            <NavButton
              key={item.id}
              item={item}
              active={item.id === activeId}
              onSelect={onSelect}
              reduce={!!reduce}
            />
          ))}
        </div>
      </div>
    </nav>
  );
}

function NavButton({
  item,
  active,
  onSelect,
  reduce,
}: {
  item: NavItem;
  active: boolean;
  onSelect: (id: string) => void;
  reduce: boolean;
}) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex w-14 flex-col items-center gap-0.5 rounded-xl py-1 text-[0.65rem] font-medium transition-colors duration-150",
        active ? "text-primary" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <span className="relative grid size-8 place-items-center">
        {active && (
          <motion.span
            layoutId={reduce ? undefined : "nav-active"}
            transition={{ type: "spring", stiffness: 480, damping: 36 }}
            className="bg-primary/12 absolute inset-0 rounded-full"
          />
        )}
        <Icon className={cn("relative size-5", active && "fill-primary/15")} />
      </span>
      {item.label}
    </button>
  );
}
